/**
 * React Final Form Validation Utilities
 *
 * Common validation functions for use with React Final Form.
 * These validators return undefined when valid, or an error message when invalid.
 */

/**
 * Validates that a value is not empty
 * @param {*} value - The value to validate
 * @returns {string|undefined} - Error message or undefined
 */
export const required = (value) => {
  const isEmpty = value === null || value === undefined || value === '';
  if (isEmpty) {
    return 'Required';
  }

  // Check for empty arrays
  if (Array.isArray(value) && value.length === 0) {
    return 'Required';
  }

  return undefined;
};

/**
 * Validates email format
 * @param {string} value - The email value to validate
 * @returns {string|undefined} - Error message or undefined
 */
export const isEmail = (value) => {
  if (!value) return undefined;

  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  return emailRegex.test(value) ? undefined : 'Invalid email address';
};

/**
 * Validates SSN format (XXX-XX-XXXX)
 * @param {string} value - The SSN value to validate
 * @returns {string|undefined} - Error message or undefined
 */
export const validateSSN = (value) => {
  if (!value) return undefined;

  const ssnRegex = /\d{3}-\d{2}-\d{4}/;
  return ssnRegex.test(value) ? undefined : 'SSN should be 9 digits';
};

/**
 * Validates phone number format
 * @param {string} value - The phone value to validate
 * @returns {string|undefined} - Error message or undefined
 */
export const validatePhone = (value) => {
  if (!value) return undefined;

  const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
  return phoneRegex.test(value) ? undefined : 'Phone number must be 10 digits';
};

/**
 * Validates that a value matches a given regex pattern
 * @param {RegExp} regex - The regex pattern to match
 * @param {string} message - The error message to return if invalid
 * @returns {Function} - Validator function
 */
export const matchesPattern = (regex, message = 'Invalid format') => (value) => {
  if (!value) return undefined;
  return regex.test(value) ? undefined : message;
};

/**
 * Validates minimum length
 * @param {number} min - Minimum length
 * @returns {Function} - Validator function
 */
export const minLength = (min) => (value) => {
  if (!value) return undefined;
  return value.length >= min ? undefined : `Must be at least ${min} characters`;
};

/**
 * Validates maximum length
 * @param {number} max - Maximum length
 * @returns {Function} - Validator function
 */
export const maxLength = (max) => (value) => {
  if (!value) return undefined;
  return value.length <= max ? undefined : `Must be no more than ${max} characters`;
};

/**
 * Validates that a number is within a range
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {Function} - Validator function
 */
export const inRange = (min, max) => (value) => {
  if (value === null || value === undefined || value === '') return undefined;
  const num = Number(value);
  if (Number.isNaN(num)) return 'Must be a number';
  if (num < min || num > max) return `Must be between ${min} and ${max}`;
  return undefined;
};

/**
 * Composes multiple validators into one
 * Validators are run in sequence until one returns an error
 * @param {...Function} validators - Validator functions to compose
 * @returns {Function} - Composed validator function
 */
export const composeValidators = (...validators) => (value) => validators.reduce(
  (error, validator) => error || validator(value),
  undefined,
);

/**
 * Creates a conditional validator that only runs if a condition is met
 * @param {Function} condition - Function that returns true if validation should run
 * @param {Function} validator - Validator to run if condition is true
 * @returns {Function} - Conditional validator function
 */
export const when = (condition, validator) => (value, allValues) => {
  if (condition(allValues)) {
    return validator(value);
  }
  return undefined;
};

/**
 * Validates that a date is not in the future
 * @param {string|Date} value - The date value to validate
 * @returns {string|undefined} - Error message or undefined
 */
export const notFutureDate = (value) => {
  if (!value) return undefined;
  const date = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date <= today ? undefined : 'Date cannot be in the future';
};

/**
 * Validates that a date is not in the past
 * @param {string|Date} value - The date value to validate
 * @returns {string|undefined} - Error message or undefined
 */
export const notPastDate = (value) => {
  if (!value) return undefined;
  const date = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date >= today ? undefined : 'Date cannot be in the past';
};
